import { Link } from "react-router-dom";
import { EnvelopeSimple, Phone, MapPin, ArrowUpRight, YoutubeLogo, InstagramLogo, LinkedinLogo } from "@phosphor-icons/react";
import { useSettings } from "@/contexts/SettingsContext";

const SOCIALS = [
  { name: "YouTube", href: "https://www.youtube.com/@OneTecGroup", Icon: YoutubeLogo },
  { name: "Instagram", href: "https://www.instagram.com/onetec_eventattitude/", Icon: InstagramLogo },
  { name: "LinkedIn", href: "https://www.linkedin.com/company/onetec-eventattitude/", Icon: LinkedinLogo },
];

export default function Footer() {
  const { settings } = useSettings();
  const address = (settings?.contact_address || "OneTec SRL\nTollaan 75\nB-1932 Zaventem").split("\n");
  const email = settings?.contact_email || "hello@onetec.eu";
  const phone = settings?.contact_phone || "+32 2 702 36 36";
  const phoneHref = `tel:${phone.replace(/\s+/g, "")}`;
  const vat = settings?.vat || "BE0478.889.493";
  const logoUrl = settings?.footer_logo_url || settings?.nav_logo_url;

  return (
    <footer className="border-t-2 border-ot-fg bg-ot-indigo text-white mt-20" data-testid="site-footer" role="contentinfo">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            {logoUrl ? (
              <img
                src={logoUrl.startsWith("/api") ? (process.env.REACT_APP_BACKEND_URL + logoUrl) : logoUrl}
                alt="OneTec"
                className="h-10 w-auto mb-6 brightness-0 invert"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : null}
            <h3 className="font-display text-5xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
              We boost your<br />
              <span className="bg-ot-peach text-ot-fg px-2">events.</span>
            </h3>
            <p className="mt-6 text-white/70 max-w-sm">
              OneTec SRL — Event technology, brand activations, and IT/AV rental across {settings?.stats?.countries || 42} countries.
            </p>
            <Link
              to="/contact"
              className="nb-btn mt-6 bg-ot-peach text-ot-fg border-ot-fg"
              data-testid="footer-cta-contact"
            >
              Start a project <ArrowUpRight weight="bold" />
            </Link>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-widest text-ot-peach font-bold mb-4">Explore</p>
            <ul className="space-y-2 font-display uppercase">
              <li><Link to="/" className="hover:text-ot-peach" data-testid="footer-link-home">Home</Link></li>
              <li><Link to="/services" className="hover:text-ot-peach" data-testid="footer-link-services">Services</Link></li>
              <li><Link to="/products" className="hover:text-ot-peach" data-testid="footer-link-products">Products</Link></li>
              <li><Link to="/about" className="hover:text-ot-peach" data-testid="footer-link-about">About</Link></li>
              <li><Link to="/contact" className="hover:text-ot-peach" data-testid="footer-link-contact">Contact</Link></li>
            </ul>
            <p className="text-xs uppercase tracking-widest text-ot-peach font-bold mt-8 mb-4">Solutions</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/event-registration-belgium" className="hover:text-ot-peach">Event Registration Belgium</Link></li>
              <li><Link to="/onsite-badging-belgium" className="hover:text-ot-peach">Onsite Badging Belgium</Link></li>
              <li><Link to="/brand-activation-belgium" className="hover:text-ot-peach">Brand Activation Belgium</Link></li>
              <li><Link to="/photobooth-rental-belgium" className="hover:text-ot-peach">Photobooth Rental Belgium</Link></li>
              <li><Link to="/event-accreditation-systems" className="hover:text-ot-peach">Event Accreditation Systems</Link></li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-widest text-ot-peach font-bold mb-4">Contact</p>
            <address className="not-italic space-y-3">
              <div className="flex items-start gap-3">
                <MapPin size={20} weight="bold" className="mt-1 shrink-0" />
                <span>{address.map((l, i) => <span key={i}>{l}<br /></span>)}</span>
              </div>
              <a href={`mailto:${email}`} className="flex items-center gap-3 hover:text-ot-peach" data-testid="footer-email">
                <EnvelopeSimple size={20} weight="bold" /> {email}
              </a>
              <a href={phoneHref} className="flex items-center gap-3 hover:text-ot-peach" data-testid="footer-phone">
                <Phone size={20} weight="bold" /> {phone}
              </a>
            </address>

            <p className="text-xs uppercase tracking-widest text-ot-peach font-bold mt-8 mb-3">Follow</p>
            <div className="flex items-center gap-3" data-testid="footer-socials">
              {SOCIALS.map(({ name, href, Icon }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`OneTec on ${name}`}
                  className="w-10 h-10 border-2 border-white flex items-center justify-center hover:bg-ot-peach hover:text-ot-fg hover:border-ot-fg transition-colors"
                  data-testid={`footer-social-${name.toLowerCase()}`}
                >
                  <Icon size={20} weight="bold" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/20 flex flex-col md:flex-row justify-between gap-3 text-sm text-white/60">
          <span>© {new Date().getFullYear()} OneTec SRL — VAT {vat}</span>
          <div className="flex flex-wrap items-center gap-4">
            <Link
              to="/privacy-policy"
              className="hover:text-ot-peach"
              data-testid="footer-link-privacy"
            >
              Privacy Policy
            </Link>
            <span className="hidden md:inline text-white/30">·</span>
            <span>Built with attitude in Brussels.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
